import { Link, Outlet } from "react-router-dom";
import { MessageCircle, Instagram, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { buildWhatsAppUrl } from "@/lib/format";

/**
 * Página exibida aos VISITANTES quando o Modo Manutenção está ativo
 * (ativado/desativado no painel: Site e Textos → Modo Manutenção).
 */
export const MaintenancePage = () => {
  const settings = useSettings();
  const waUrl = buildWhatsAppUrl(
    "Olá! Vi que a loja está em manutenção e gostaria de fazer um pedido ou tirar uma dúvida.",
    settings.whatsapp_number
  );
  return (
    <div
      className="min-h-screen bg-[#06254f] flex flex-col items-center justify-center px-6 text-center relative overflow-hidden"
      data-testid="maintenance-page"
    >
      {/* brilhos decorativos discretos */}
      <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-brand/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-secondary/10 blur-3xl pointer-events-none" />

      <img
        src="/assets/logo.png"
        alt="HG Móveis"
        className="w-24 h-24 sm:w-28 sm:h-28 rounded-full shadow-2xl ring-4 ring-white/10"
        data-testid="maintenance-logo"
      />
      <h1
        className="mt-8 font-heading text-2xl sm:text-4xl font-bold text-white tracking-tight max-w-xl"
        data-testid="maintenance-title"
      >
        Estamos preparando novidades.
      </h1>
      <p className="mt-3 text-base sm:text-lg text-blue-100/80 max-w-md" data-testid="maintenance-subtitle">
        Voltaremos em breve!
      </p>

      <div className="mt-10 flex flex-col sm:flex-row items-center gap-3">
        <a href={waUrl} target="_blank" rel="noreferrer">
          <span
            className="inline-flex items-center gap-2.5 bg-[#25D366] hover:bg-[#1fb857] text-white font-semibold rounded-full px-7 py-3.5 shadow-lg shadow-black/20 transition-all hover:scale-[1.03] active:scale-95"
            data-testid="maintenance-whatsapp"
          >
            <MessageCircle className="w-5 h-5" />
            WhatsApp {settings.whatsapp_display || "(21) 98243-0311"}
          </span>
        </a>
        {settings.instagram_url && (
          <a
            href={settings.instagram_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-blue-100/70 hover:text-white text-sm font-medium transition-colors"
            data-testid="maintenance-instagram"
          >
            <Instagram className="w-4 h-4" /> Siga a HG Móveis
          </a>
        )}
      </div>

      <p className="mt-10 text-xs text-blue-100/50 max-w-sm">
        Pagamento na entrega • Entrega rápida em várias regiões do RJ
      </p>

      <Link
        to="/admin/login"
        className="mt-14 inline-flex items-center gap-1.5 text-[11px] text-blue-100/40 hover:text-blue-100/80 transition-colors"
        data-testid="maintenance-admin-link"
      >
        <Lock className="w-3 h-3" /> Acesso administrativo
      </Link>
    </div>
  );
};

/**
 * Portão da loja: com manutenção ativa, visitantes veem a MaintenancePage;
 * administradores autenticados continuam navegando e testando a loja normalmente.
 */
export const MaintenanceGate = () => {
  const settings = useSettings();
  const { user } = useAuth(); // null = verificando | false = visitante | objeto = admin

  if (!settings.maintenance_mode) return <Outlet />;
  if (user === null) {
    return <div className="min-h-screen bg-[#06254f]" data-testid="maintenance-loading" />;
  }
  if (user) {
    return (
      <>
        <div
          className="sticky top-0 z-[90] bg-amber-400 text-slate-900 text-center text-xs sm:text-sm font-semibold py-2 px-4"
          data-testid="maintenance-admin-banner"
        >
          Modo manutenção ATIVO — os visitantes veem a página "em breve", mas você pode navegar e testar a loja
          normalmente. <Link to="/admin/configuracoes" className="underline underline-offset-2">Desativar no painel</Link>
        </div>
        <Outlet />
      </>
    );
  }
  return <MaintenancePage />;
};
